/**
 * Parse slash command input
 * Based on dexto's parseSlashInput implementation
 */

export interface SlashParse {
  isSlash: boolean;
  command: string;
  argsArray: string[];
  argsText: string;
}

/**
 * Parse quoted arguments from a string
 * Supports single quotes, double quotes, and unquoted tokens
 */
function parseQuotedArguments(input: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let inQuote: string | null = null;
  let escaped = false;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }

    if (char === '\\') {
      escaped = true;
      continue;
    }

    if (inQuote) {
      if (char === inQuote) {
        inQuote = null;
      } else {
        current += char;
      }
    } else {
      if (char === '"' || char === "'") {
        inQuote = char;
      } else if (char === ' ' || char === '\t') {
        if (current) {
          tokens.push(current);
          current = '';
        }
      } else {
        current += char;
      }
    }
  }

  if (current) {
    tokens.push(current);
  }

  return tokens;
}

/**
 * Parse slash command input into command and arguments
 */
export function parseSlashInput(input: string): SlashParse {
  const trimmed = input.trim();
  if (!trimmed.startsWith('/')) {
    return { isSlash: false, command: '', argsArray: [], argsText: '' };
  }

  const parts = parseQuotedArguments(trimmed.slice(1));
  const command = parts[0] || '';
  const argsArray = parts.slice(1);
  const argsText = argsArray.join(' ');

  return { isSlash: true, command, argsArray, argsText };
}

/**
 * Split arguments into key-value pairs and positional arguments
 */
export function splitKeyValueAndPositional(args: string[]): {
  keyValues: Record<string, string>;
  positional: string[];
} {
  const keyValues: Record<string, string> = {};
  const positional: string[] = [];

  for (const arg of args) {
    const eqIndex = arg.indexOf('=');
    if (eqIndex > 0 && eqIndex < arg.length - 1) {
      const key = arg.slice(0, eqIndex);
      const value = arg.slice(eqIndex + 1);
      keyValues[key] = value;
    } else {
      positional.push(arg);
    }
  }

  return { keyValues, positional };
}

/**
 * Build arguments payload for prompt resolution
 */
export function buildArgsPayload(argsArray: string[]): Record<string, unknown> {
  if (argsArray.length === 0) {
    return {};
  }

  const { keyValues, positional } = splitKeyValueAndPositional(argsArray);
  const argsPayload: Record<string, unknown> = { ...keyValues };

  if (positional.length > 0) {
    argsPayload._positional = positional;
  }

  return argsPayload;
}
